<template>
  <div class="page-row ship-page"
       :class="{ '--expired': !connected }">
    <div class="page-row-top">
      <PageAvatar :page="page" />
      <div class="who">
        <b>{{ page.name }}</b>
        <span v-if="shopLine"
              class="ship-shop">{{ shopLine }}</span>
        <button v-if="needsShop"
                type="button"
                class="btn btn-outline-primary btn-sm ship-pick-link"
                @click="$emit('change-shop', page)">{{ $t("messenger.ship.choose_shop") }}</button>
      </div>
      <div class="ship-status">
        <span class="badge badge-pill status-badge --padded"
              :class="connected ? '--success' : '--warning'">{{ connected ? $t("messenger.ship.status_connected") : $t("messenger.ship.status_reconnect") }}</span>
        <button v-if="!connected"
                type="button"
                class="btn btn-primary btn-sm"
                @click="$emit('reconnect', page)">{{ $t("messenger.ship.reconnect") }}</button>
      </div>
    </div>

    <div class="ship-feats">
      <div class="ship-feat">
        <span class="ship-feat-name">{{ $t("messenger.ship.feat_tracking") }}</span>
        <span class="badge badge-pill status-badge pill"
              :class="'--' + trackPill.variant">{{ trackPill.label }}</span>
        <template v-if="showRejectedNote">
          <span class="tpl-dot"
                :title="$t('messenger.ship.rejected_note')"></span><span class="tpl-fail">{{ $t("messenger.ship.rejected_note") }}</span>
        </template>
        <button type="button"
                class="btn btn-outline-primary btn-sm"
                :disabled="!connected"
                @click="$emit('view-message', page)">{{ $t("messenger.ship.view_message") }}</button>
      </div>

      <div class="ship-feat">
        <span class="ship-feat-name">{{ $t("messenger.ship.feat_ship") }}</span>
        <span class="badge badge-pill status-badge pill"
              :class="'--' + shipPill.variant">{{ shipPill.label }}</span>
        <Button variant="outline-primary"
                size="sm"
                :loading="shipBusy"
                :disabled="!connected || shipBusy"
                @click="toggleShip">{{ shipOn ? $t("messenger.ship.turn_off") : $t("messenger.ship.turn_on") }}</Button>
      </div>

      <p class="ship-why">{{ $t("messenger.ship.pitch") }}
        <button type="button"
                class="btn btn-outline-primary btn-sm"
                @click="$emit('how-to', page)">{{ $t("messenger.ship.how_to") }}</button>
      </p>
    </div>
  </div>
</template>

<script>
import Button from "@/components/core/Button";
import PageAvatar from "@/components/derived/messenger/PageAvatar";
import { isPageConnected } from "@/store/modules/messenger";
import { templateReviewStatus, escapeHtml } from "@/components/derived/messenger/format";
import { Toast } from "@/helpers/toastr";

// One connected Page on the เพจ Facebook card: status, shop link and the two features.
// Reads its own ship-settings (cached per Page in the store) and tracking summary.
export default {
  name: "PageRow",
  components: { Button, PageAvatar },
  props: {
    page: { type: Object, required: true },
  },

  data() {
    return {
      settings: null,
      shipBusy: false,
      tplState: "idle", // idle | loading | ready | error
      tplReview: null,
    };
  },

  created() {
    this.loadSettingsIfNeeded();
    if (this.connected) this.loadTemplate();
  },

  computed: {
    connected() {
      return isPageConnected(this.page);
    },

    // ship-settings shops: [{shopId, name}], the ProShip shops that hold this Page
    settingShops() {
      return this.settings && Array.isArray(this.settings.shops) ? this.settings.shops : [];
    },

    // เลือกร้าน only once ship-settings answered and no shop holds the Page
    needsShop() {
      return this.settings != null && this.settingShops.length === 0;
    },

    shopLine() {
      const names = this.settingShops.map((shop) => shop.name).filter(Boolean);
      return names.length ? this.$t("messenger.ship.shop_line", { name: names.join(", ") }) : null;
    },

    shipOn() {
      if (this.page.shipEnabled != null) {
        if (this.settings && this.settings.ship_enabled != null) return this.settings.ship_enabled !== false;
        return this.page.shipEnabled !== false;
      }
      if (this.settings && this.settings.ship_enabled != null) return this.settings.ship_enabled !== false;
      return false;
    },

    trackPill() {
      if (!this.connected) return { variant: "warning", label: this.$t("messenger.ship.pill_unsendable") };
      if (this.tplState !== "ready") return { variant: "muted", label: "…" };
      if (this.tplReview === "PENDING") return { variant: "warning", label: this.$t("messenger.ship.pill_pending") };
      return { variant: "info", label: this.$t("messenger.ship.pill_auto") };
    },

    showRejectedNote() {
      return this.connected && this.tplState === "ready" && this.tplReview === "REJECTED";
    },

    shipPill() {
      if (!this.connected) return { variant: "warning", label: this.$t("messenger.ship.pill_unsendable") };
      return this.shipOn
        ? { variant: "success", label: this.$t("messenger.ship.pill_on") }
        : { variant: "muted", label: this.$t("messenger.ship.pill_off") };
    },
  },

  methods: {
    loadSettingsIfNeeded() {
      this.$store.dispatch("messenger/fetchShipSettings", {
        pageId: this.page.pageId,
        callback: (ok, data) => {
          if (this._isDestroyed) return;
          if (ok && data) this.settings = data;
        },
      });
    },

    loadTemplate() {
      this.tplState = "loading";
      this.$store.dispatch("messenger/fetchTemplate", {
        pageId: this.page.pageId,
        callback: (ok, data) => {
          if (this._isDestroyed) return;
          if (!ok || !data || !data.shipped) {
            this.tplState = "error";
            return;
          }
          this.tplReview = templateReviewStatus(data);
          this.tplState = "ready";
        },
      });
    },

    toggleShip() {
      if (this.shipBusy || !this.connected) return;
      const next = !this.shipOn;
      this.shipBusy = true;
      this.$store.dispatch("messenger/updateShipSettings", {
        pageId: this.page.pageId,
        shipEnabled: next,
        callback: (ok) => {
          this.shipBusy = false;
          if (this._isDestroyed) return;
          if (!ok) {
            Toast(this.$t("messenger.pages.action_failed"), "danger");
            return;
          }
          this.settings = Object.assign({}, this.settings, { ship_enabled: next });
          Toast(this.$t(next ? "messenger.ship.ship_on_toast" : "messenger.ship.ship_off_toast", { name: escapeHtml(this.page.name) }), "success");
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.page-row {
  background: $dashboard-background;
  border-radius: 10px;
  padding: 1rem 1.1rem;

  &.--expired {
    background: #fdf1f1;
  }
}

.page-row-top {
  display: flex;
  align-items: center;
  gap: 0.85rem;

  .who {
    flex: 1 1 auto;
    min-width: 0;

    b {
      color: map-get($text-colors, 2);
      font-size: 1.05rem;
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.ship-page .btn {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ship-page .who .ship-shop {
  display: block;
  color: #aaa;
  font-size: 0.875rem;
  margin-top: 0.15rem;
}

.ship-page .who .ship-pick-link {
  margin-top: 0.4rem;
}

.ship-status {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
  flex: 0 0 auto;
}

.ship-feats {
  margin-top: 0.85rem;
  padding-left: calc(40px + 0.85rem);
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

@media (max-width: 767.98px) {
  .ship-feats {
    padding-left: 0;
  }
}

.ship-feat {
  display: flex;
  align-items: center;
  gap: 0.5rem 0.75rem;
  flex-wrap: wrap;
}

.ship-feat-name {
  color: #333;
  font-size: 1rem;
  flex: 1 1 10rem;
  min-width: 0;
}

.ship-feat .btn {
  margin-left: auto;
  min-width: 64px;
}

.ship-why {
  color: #aaa;
  font-size: 0.875rem;
  margin: 0;
}

.status-badge {
  &.--padded {
    padding: 0.4rem 1.1rem;
  }

  &.--success {
    background-color: rgba($brand-green-mid, 0.1);
    color: $brand-green-mid;
  }

  &.--warning {
    background-color: rgba(255, 152, 0, 0.1);
    color: #e68900;
  }

  &.--info {
    background-color: rgba(39, 170, 224, 0.1);
    color: #1d8fbf;
  }

  &.--muted {
    background-color: rgba(158, 158, 158, 0.1);
    color: #8a8a8a;
  }
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.75rem;
  border-radius: 10rem;
  font-size: 0.8125rem;
  font-weight: 700;
  white-space: nowrap;
}

.tpl-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d32f2f;
  margin-left: 0.35rem;
}

.tpl-fail {
  color: #c62828;
  font-size: 0.8125rem;
  font-weight: 700;
  margin-left: 0.3rem;
  white-space: nowrap;
}

body:not(.--dark) {
  .status-badge.--success {
    color: #1a6e2e;
  }

  .status-badge.--warning {
    color: #9a5a00;
  }

  .status-badge.--info {
    color: #1d6f94;
  }

  .status-badge.--muted {
    color: #666;
  }

  .ship-page .ship-shop,
  .ship-why {
    color: #666;
  }

  .ship-page .btn-outline-primary:not(:hover) {
    background: #fff;
  }
}

body.--dark {
  .ship-page .btn-outline-primary {
    color: #8cc8f5;
    border-color: #8cc8f5;
  }

  .ship-page .btn-outline-primary:hover:not(:disabled) {
    color: #fff;
  }

  .ship-feat-name {
    color: lightgray;
  }

  .tpl-fail {
    color: #ff9d9d;
  }
}

@media (max-width: 767.98px) {
  .pill {
    font-size: 12px;
  }
}
</style>
